import Swiper from "swiper";
import { Navigation, Pagination, Autoplay , FreeMode , Mousewheel} from "swiper/modules";
import { animateInObserver } from './_scrollIn.js';
import { lenisInstance } from './_lenis.js';  // lenisInstanceをインポート

export const performancePosts= {

  relatedPosts: [],
  currentIndex: 0,
  animationDelay: 500,
  touchStartX: 0,
  touchEndX: 0,
  touchStartY: 0,
  touchEndY: 0,
  minSwipeDistance: 50,
  maxVerticalDistance: 30,
  performanceEl: null,
  paginationEl: null,
  searchState: {
    business: [],
    area: [],
    property: [],
    currentPage: 1,
    totalPages: 0,
    totalPosts: 0,
    filteredPostIds: [],
  },
  cachedPosts: {},

  async init(clickedItem = null) {
    this.performanceEl = document.querySelector('.js-performance-posts');
    this.paginationEl = this.performanceEl?.querySelector('.pagination') || null;
    if (!this.performanceEl) return;

    this.initSearch();

    const slug = this.performanceEl.dataset.slug || null;
    const useSwiper = this.performanceEl.dataset.swiper === 'true';
    const postID = clickedItem?.dataset?.id || null;

    try {
      // slugがある場合は検索条件に追加
      if (slug) {
        this.searchState.business = [slug];
      }

      // 投稿を取得
      await this.fetchPosts();

      // クリックされた投稿がある場合はモーダルを表示
      if (postID) {
        const parsedId = parseInt(postID);
        this.currentIndex = this.searchState.filteredPostIds.indexOf(parsedId);
        if (this.currentIndex !== -1) {
          this.showModal();
        }
      }

      // Swiperの初期化
      if (useSwiper) {
        const swiperEl = this.performanceEl.querySelector('.performance-swiper');
        if (swiperEl) {
          const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
          if (slideCount <= 4) {
            swiperEl?.classList.add('is-few-slides');
          }
          
          new Swiper(swiperEl, {
            modules: [Autoplay, FreeMode],
            slidesPerView: 1.1,
            spaceBetween: 20,
            loop: slideCount > 4,
            speed: 800,
            freeMode: true,
            autoplay: {
              delay: 5000,
              disableOnInteraction: false,
              enabled: slideCount > 4
            },
            breakpoints: {
              768: {
                slidesPerView: Math.min(3.2, slideCount),
              },
              1480: {
                slidesPerView: Math.min(4.2, slideCount),
              },
            },
            watchOverflow: true,
            observer: true,
            observeParents: true
          });
        }
      }
      
    } catch(error) {
      console.error('Error:', error);
    }

    // モーダルのスクロール制御を初期化
    const modal = document.querySelector('.performance-modal');
    if (modal) {
      const modalContent = modal.querySelector('.performance-modal-content');
      if (modalContent) {
        // モーダルコンテンツ全体のスクロールを有効化
        modalContent.addEventListener('wheel', (e) => {
          e.stopPropagation();  // Lenisのスクロール制御を停止
        }, { passive: true });

        // タッチデバイス用のスクロール制御
        modalContent.addEventListener('touchmove', (e) => {
          e.stopPropagation();  // Lenisのスクロール制御を停止
        }, { passive: true });
      }
    }
  },

  // 検索イベントの初期化
  initSearch() {
    const searchSelect = document.querySelector('.search-select');
    if (!searchSelect) return;

    // 初期状態で「すべて」を選択状態に
    searchSelect.querySelectorAll('.search-select-field').forEach(field => {
      const allLabel = field.querySelector('.is-all');
      if (allLabel) {
        allLabel.classList.add('is-current');
      }
    });

    searchSelect.querySelectorAll('.search-select-field').forEach(field => {
      const taxonomy = field.dataset.taxonomy;
      const allLabel = field.querySelector('.is-all');
      const checkboxes = field.querySelectorAll('.search-checkbox');

      // すべてラベルのクリックイベント
      allLabel?.addEventListener('click', () => {
        // チェックボックスをすべて解除
        checkboxes.forEach(cb => cb.checked = false);
        
        // すべてのラベルのみcurrent状態に
        field.querySelectorAll('.search-select-item').forEach(item => {
          item.classList.remove('is-current');
        });
        allLabel.classList.add('is-current');
        
        // 検索条件をリセット
        this.searchState[taxonomy] = [];
        this.searchState.currentPage = 1;
        this.fetchPosts();
      });

      // チェックボックスの変更イベント
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          const checkedBoxes = Array.from(checkboxes).filter(cb => cb.checked);
          
          // すべてのラベルの状態を制御
          if (checkedBoxes.length > 0) {
            allLabel.classList.remove('is-current');
          } else {
            allLabel.classList.add('is-current');
          }
          
          // 検索条件を更新
          this.searchState[taxonomy] = checkedBoxes.map(cb => cb.value);
          this.searchState.currentPage = 1;
          this.fetchPosts();
        });
      });
    });

    // ページ送りの初期化
    if (!this.paginationEl) return;
    this.paginationEl.addEventListener('click', (e) => {
      const target = e.target;
      let shouldScroll = false;
  
      if (target.classList.contains('pagination-num')) {
        const page = parseInt(target.textContent);
        if (!isNaN(page)) {
          this.searchState.currentPage = page;
          shouldScroll = true;
        }
      } else if (target.classList.contains('pagination-prev')) {
        if (this.searchState.currentPage > 1) {
          this.searchState.currentPage--;
          shouldScroll = true;
        }
      } else if (target.classList.contains('pagination-next')) {
        if (this.searchState.currentPage < this.searchState.totalPages) {
          this.searchState.currentPage++;
          shouldScroll = true;
        }
      }

      if (shouldScroll) {
        // 投稿を取得
        this.fetchPosts().then(() => {
          // 投稿一覧の先頭へスクロール
          const elementPosition = this.performanceEl.getBoundingClientRect().top + window.pageYOffset;
          const offset = 100; // スクロール位置を100px上に調整
          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'instant'
          });
        });
      }
    });
  },

  // フィールドからタクソノミー名を取得
  getTaxonomyFromField(field) {
    if (field.textContent.includes('事業内容')) return 'business';
    if (field.textContent.includes('エリア')) return 'area';
    if (field.textContent.includes('物件種別')) return 'property';
    return '';
  },
  
  // 投稿を取得して表示
  async fetchPosts() {
    this.performanceEl = document.querySelector('.js-performance-posts');
    if (!this.performanceEl) return;

    const restUrl = this.performanceEl.dataset.restUrl;
    const perPage = Number(this.performanceEl.dataset.perPage || '12');
    const useSwiper = this.performanceEl.dataset.swiper === 'true';

    try {
      
      // フィルタリング条件に基づいて全投稿数を取得
      const allPostsParams = new URLSearchParams({
        per_page: -1,
        ...(this.searchState.business.length > 0 && { business: this.searchState.business.join(',') }),
        ...(this.searchState.area.length > 0 && { area: this.searchState.area.join(',') }),
        ...(this.searchState.property.length > 0 && { property: this.searchState.property.join(',') })
      });

      const allPostsResponse = await fetch(`${restUrl}?${allPostsParams.toString()}`);
      if (!allPostsResponse.ok) throw new Error('Network response was not ok');
      const allPostsData = await allPostsResponse.json();

      // フィルタリングされた投稿IDリストをコンソールに表示
      this.searchState.filteredPostIds = allPostsData.posts.map(post => post.id);
      // console.log('フィルタリング後の投稿IDリスト:', this.searchState.filteredPostIds);

      // フィルタリング結果の保存と計算
      this.searchState.totalPosts = this.searchState.filteredPostIds.length;
      this.searchState.totalPages = Math.ceil(this.searchState.totalPosts / perPage);

      // 現在のページが範囲外の場合は調整
      if (this.searchState.currentPage > this.searchState.totalPages) {
        this.searchState.currentPage = Math.max(1, this.searchState.totalPages);
      }

      // 現在のページの投稿を取得
      const startIndex = (this.searchState.currentPage - 1) * perPage;
      const endIndex = Math.min(startIndex + perPage, this.searchState.filteredPostIds.length);
      const currentPageIds = this.searchState.filteredPostIds.slice(startIndex, endIndex);

      // 現在のページの投稿データを取得
      this.relatedPosts = await Promise.all(
        currentPageIds.map(id => this.fetchPostById(id))
      );


      await this.renderPosts(this.performanceEl, useSwiper);
      this.setupEventListeners();
      this.updateTotalCount();
      this.updatePagination();
      this.updateSearchResult();

    } catch(error) {
      console.error('投稿の取得でエラーが発生:', error);
    }
  },

  // 該当件数の表示を更新
  updateTotalCount() {
    const totalNumEl = document.querySelector('[data-total-num]');
    if (totalNumEl) {
      totalNumEl.textContent = this.searchState.totalPosts;
    }
  },
  

  // ページネーションの表示を更新
  updatePagination() {
    if (!this.paginationEl) return;
    let paginationHTML = '';

    // 前へのボタン
    const isPrevDisabled = this.searchState.currentPage <= 1;
    paginationHTML += `<div class="pagination-prev ${isPrevDisabled ? 'is-disabled' : ''}"> < </div>`;
    
    // ページ番号の生成過程をデバッグ
    const visiblePages = [];
    for (let i = 1; i <= this.searchState.totalPages; i++) {
      if (
        i === 1 || 
        i === this.searchState.totalPages || 
        (i >= this.searchState.currentPage - 1 && i <= this.searchState.currentPage + 1)
      ) {
        visiblePages.push({
          type: 'number',
          value: i,
          isCurrent: i === this.searchState.currentPage
        });
      } else if (
        i === this.searchState.currentPage - 2 || 
        i === this.searchState.currentPage + 2
      ) {
        visiblePages.push({
          type: 'separator',
          position: i
        });
      }
    }

    // ページ番号のHTML生成
    visiblePages.forEach(page => {
      if (page.type === 'number') {
        paginationHTML += `<div class="pagination-num ${page.isCurrent ? 'current' : ''}">${page.value}</div>`;
      } else {
        paginationHTML += `<div class="pagination-sepalate">...</div>`;
      }
    });

    // 次へボタン
    const isNextDisabled = this.searchState.currentPage >= this.searchState.totalPages;
    paginationHTML += `<div class="pagination-next ${isNextDisabled ? 'is-disabled' : ''}">></div>`;

    this.paginationEl.innerHTML = paginationHTML;
  },

  updateSearchResult() {
    const resultEl = this.performanceEl.querySelector('.performance-post-result');
    if (!resultEl) return;
  
    // 検索条件の表示用HTML
    let resultHTML = '';
  
    // 選択された検索条件を表示
    const conditions = [];
    
    // 事業内容
    if (this.searchState.business.length > 0) {
      const terms = document.querySelectorAll('[data-taxonomy="business"] .search-checkbox:checked');
      terms.forEach(term => {
        conditions.push(`<span class="search-condition-tag">#${term.nextElementSibling.textContent.trim().replace('#', '')}</span>`);
      });
    }
    
    // エリア
    if (this.searchState.area.length > 0) {
      const terms = document.querySelectorAll('[data-taxonomy="area"] .search-checkbox:checked');
      terms.forEach(term => {
        conditions.push(`<span class="search-condition-tag">#${term.nextElementSibling.textContent.trim().replace('#', '')}</span>`);
      });
    }
    
    // 物件種別
    if (this.searchState.property.length > 0) {
      const terms = document.querySelectorAll('[data-taxonomy="property"] .search-checkbox:checked');
      terms.forEach(term => {
        conditions.push(`<span class="search-condition-tag">#${term.nextElementSibling.textContent.trim().replace('#', '')}</span>`);
      });
    }

    // 検索条件の表示部分
    resultHTML = `
      <div class="search-conditions">
        <span class="search-condition-label">検索条件：</span>
        ${conditions.length > 0 ? conditions.join('') : '<span class="search-condition-tag">すべて</span>'}
      </div>
    `;

    // 0件の場合のメッセージを追加
    if (this.searchState.totalPosts === 0) {
      resultHTML += `
        <div class="search-result-empty">
          <p>該当する実績が見つかりませんでした。</p>
        </div>
      `;
    }

    resultEl.innerHTML = resultHTML;
  },

  async renderPosts(container, swiper = false) {
    const renderWrapper = container.querySelector('.performance-render-wrapper');
    if (!renderWrapper) return;

    const postsHTML = this.relatedPosts.map(post => {
      const businessTerms = post.terms?.business?.map(term => `<div class="tag-text">#${term.name}</div>`).join('') || '';
      const areaTerms = post.terms?.area?.map(term => `<div class="tag-text">#${term.name}</div>`).join('') || '';
      const propertyTerms = post.terms?.property?.map(term => `<div class="tag-text">#${term.name}</div>`).join('') || '';

      return `
        <div class="performance-post-item ${swiper ? 'swiper-slide' : ''}" data-id="${post.id}">
          <div class="performance-post-thumbnail">
            <img src="${post.thumbnail || ''}" alt="${post.title || ''}" loading="eager" decoding="async">
          </div>
          <div class="performance-post-title">
            ${post.title || ''}
          </div>
          <div class="performance-post-tags">
            ${businessTerms}
            ${areaTerms}
            ${propertyTerms}
          </div>
          <button class="performance-post-link">
            詳細を見る
          </button>
        </div>
      `;
    }).join('');
    renderWrapper.innerHTML = postsHTML;

    animateInObserver();
  },

  setupEventListeners() {
    document.querySelectorAll('.performance-post-item, .performance-item').forEach(item => {
      item.removeEventListener('click', this.handlePostClick);
      item.addEventListener('click', this.handlePostClick.bind(this));
    });

    const modal = document.querySelector('.performance-modal');
    if (!modal) return;

    modal.removeEventListener('click', this.handleModalClick);

    this.handleModalClick = (e) => {
      if (e.target.closest('.performance-modal-close')) {
        this.closeModal();
      }

      if (e.target.closest('.next-post-btn')) {
        this.nextPost();
      }

      if (e.target.closest('.prev-post-btn')) {
        this.prevPost();
      }
    };

    modal.addEventListener('click', this.handleModalClick.bind(this));
    modal.addEventListener('touchstart', this.handleTouchStart.bind(this));
    modal.addEventListener('touchend', this.handleTouchEnd.bind(this));
  },

  handlePostClick(e) {
    const item = e.currentTarget;
    const id = parseInt(item.dataset.id);
    if (id) {
  
      // filteredPostIdsから該当する投稿のインデックスを探す
      this.currentIndex = this.searchState.filteredPostIds.indexOf(id);
      
      if (this.currentIndex !== -1) {
        this.showModal();
        document.documentElement.classList.add('is-modal-open');
      }
    }
  },

  async showModal() {
    try {
      const currentPostId = this.searchState.filteredPostIds[this.currentIndex];
      
      if (!currentPostId) return;

      const currentPost = await this.fetchPostById(currentPostId);
      
      if (!currentPost) return;

      const modal = document.querySelector('.performance-modal');
      const modalContent = modal.querySelector('.performance-modal-content');
      
      const isMobile = window.innerWidth <= 768;
      const thumbnailUrl = isMobile ? 
        (currentPost.thumbnail_mobile || currentPost.thumbnail) : 
        currentPost.thumbnail;

      const propertyTerms = currentPost.terms?.property?.map(term => term.name) || [];
      
      const tableDatas = {
        'エリア': currentPost.terms?.area?.[0]?.name || '',
        '所在': currentPost.meta?.address || '',
        '種別': propertyTerms.join(', '),
        '構造': currentPost.meta?.structure || '',
        '築年数': currentPost.meta?.building_age || '',
        '総戸数': currentPost.meta?.number_units || '',
        '土地面積': currentPost.meta?.land_area || '',
        '建物面積': currentPost.meta?.building_area || '',
      };

      if (currentPost.meta?.add_contents && Array.isArray(currentPost.meta.add_contents)) {
        currentPost.meta.add_contents.forEach(content => {
          if (content.title && content.text) {
            tableDatas[content.title] = content.text.replace(/\r\n|\r|\n/g, '<br>');
          }
        });
      }

      tableDatas['備考'] = (currentPost.meta?.remarks || '').replace(/\r\n|\r|\n/g, '<br>');

      modalContent.innerHTML = `
          <div class="performance-modal-close display-tb-down">
            <span>閉じる<i class="icon icon-plus"></i></span>
          </div>
          <div class="performance-modal-image">
            <div class="loading-placeholder">
              <div class="spinner"></div>
            </div>
            <picture>
              ${isMobile ? `<source media="(max-width: 768px)" srcset="${currentPost.thumbnail_mobile || ''}">` : ''}
              <img src="${thumbnailUrl}" alt="${currentPost.title || ''}" loading="eager" decoding="async" onload="this.parentElement.parentElement.querySelector('.loading-placeholder')?.remove()">
            </picture>
          </div>
          <div class="performance-modal-info">
            <div class="business-name">${currentPost.terms?.business?.[0]?.name || ''}</div>
            <div class="performance-name">${currentPost.title || ''}</div>
            
            <table class="performance-table">
              ${Object.entries(tableDatas)
                .filter(([_, value]) => value)
                .map(([key, value]) => `<tr><th>${key}</th><td>${value}</td></tr>`)
                .join('')}
            </table>
          </div>
      `;

      modal.classList.add('is-open');
      setTimeout(() => {
        modal.classList.add('is-visible');
      }, 50);
      
      // Lenisの制御のみ
      if (lenisInstance) {
        lenisInstance.stop();
      }

      this.updateNavigationButtons();
    } catch (error) {
      console.error('Error showing modal:', error);
    }
  },

  updateNavigationButtons() {
    const prevBtn = document.querySelector('.prev-post-btn');
    const nextBtn = document.querySelector('.next-post-btn');
    
    if (prevBtn) {
      if (this.currentIndex > 0) {
        prevBtn.classList.add('is-active');
        prevBtn.classList.remove('is-inactive');
      } else {
        prevBtn.classList.add('is-inactive');
        prevBtn.classList.remove('is-active');
      }
    }
    
    if (nextBtn) {
      if (this.currentIndex < this.searchState.filteredPostIds.length - 1) {
        nextBtn.classList.add('is-active');
        nextBtn.classList.remove('is-inactive');
      } else {
        nextBtn.classList.add('is-inactive');
        nextBtn.classList.remove('is-active');
      }
    }
  },

  closeModal() {
    const modal = document.querySelector('.performance-modal');
    
    modal.classList.remove('is-open', 'is-visible', 'is-visible-prev');
    document.documentElement.classList.remove('is-modal-open');
    
    // 進行中のタイマーをクリア
    if (this.modalTimer) {
      clearTimeout(this.modalTimer);
      this.modalTimer = null;
    }
    
    // Lenisの制御を元に戻す
    if (lenisInstance) {
      lenisInstance.start();
    }
  },

  async nextPost() {
    if (this.currentIndex < this.searchState.filteredPostIds.length - 1) {
      const modalContent = document.querySelector('.performance-modal-content');
      const modal = document.querySelector('.performance-modal');
      
      // アニメーション中は処理をスキップ
      if (modal.classList.contains('is-visibling')) return;
      
      if (modalContent) {
        modalContent.scrollTo({
          top: 0,
          behavior: 'instant'
        });
      }

      modal.classList.add('is-visibling');
      modal.classList.remove('is-visible');

      this.currentIndex++;
      
      // タイマーIDを保存
      this.modalTimer = setTimeout(() => {
        this.showModal();
        modal.classList.remove('is-visibling');
        modal.classList.add('is-visible');
      }, this.animationDelay);
    }
  },

  async prevPost() {
    if (this.currentIndex > 0) {
      const modalContent = document.querySelector('.performance-modal-content');
      const modal = document.querySelector('.performance-modal');
      
      // アニメーション中は処理をスキップ
      if (modal.classList.contains('is-visibling-prev')) return;
      
      if (modalContent) {
        modalContent.scrollTo({
          top: 0,
          behavior: 'instant'
        });
      }

      modal.classList.add('is-visibling-prev');
      modal.classList.remove('is-visible');

      this.currentIndex--;
      
      // タイマーIDを保存
      this.modalTimer = setTimeout(() => {
        this.showModal();
        modal.classList.remove('is-visibling-prev');
        modal.classList.add('is-visible');
      }, this.animationDelay);
    }
  },

  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].pageY;
  },

  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].clientX;
    this.touchEndY = e.changedTouches[0].pageY;
    this.handleSwipe();
  },

  handleSwipe() {
    const swipeDistance = this.touchEndX - this.touchStartX;
    const verticalDistance = Math.abs(this.touchEndY - this.touchStartY);
    
    if (verticalDistance > this.maxVerticalDistance) {
      return;
    }
    
    if (Math.abs(swipeDistance) >= this.minSwipeDistance) {
      const modal = document.querySelector('.performance-modal');
      
      modal.style.overflowY = 'hidden';
      
      if (swipeDistance > 0) {
        if (this.currentIndex > 0) {
          this.prevPost();
        }
      } else {
        if (this.currentIndex < this.searchState.filteredPostIds.length - 1) {
          this.nextPost();
        }
      }
      
      setTimeout(() => {
        modal.style.overflowY = 'scroll';
      }, this.animationDelay);
    }
  },

  // 投稿詳細を取得する関数を追加
  async fetchPostById(postId) {
    // すでにキャッシュにある場合はそれを返す
    if (this.cachedPosts[postId]) {
      return this.cachedPosts[postId];
    }

    // キャッシュにない場合はAPIから取得
    try {
      const restUrl = this.performanceEl.dataset.restUrl;
      const response = await fetch(`${restUrl}?id=${postId}`);
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      
      // APIからの応答が期待通りか確認
      if (!data.posts || !data.posts[0]) {
        throw new Error('Invalid response format');
      }

      // キャッシュに保存
      this.cachedPosts[postId] = data.posts[0];

      return data.posts[0];
    } catch (error) {
      console.error('Error fetching post:', error);
      throw error;
    }
  }
}